// src/lib/opportunity/formatRegistry.ts
//
// Pricing-format dispatcher. The opportunity engine keeps two format_key
// values on snapshots for PDF branching:
//   - `generic` — manager-driven subtotal + calc lines + tax/deposit
//   - `red_iron`  — legacy snapshots only (five hard-coded cost lines)
//
// New quotes always persist `format_key = generic`. Manager presets
// (calc lines, deposit rules, proposal text) are keyed separately via
// `resolveManagerPresetFormatKey` so a product_type like `red_iron`
// can still have its own manager template stored under format_key
// `red_iron` without reviving the legacy sales form.

import type { OpportunityFormatKey } from "../../types/opportunity.js";

export type OpportunityFormatDef = {
  key: OpportunityFormatKey;
  label: string;
  /** One-line description for the format picker / docs. */
  description: string;
};

export const OPPORTUNITY_FORMATS: Record<OpportunityFormatKey, OpportunityFormatDef> = {
  red_iron: {
    key: "red_iron",
    label: "Red Iron (legacy)",
    description:
      "Legacy five named cost line items. Retained for older snapshots only.",
  },
  generic: {
    key: "generic",
    label: "Generic",
    description:
      "Manager-configured subtotal, calc lines, tax, and deposit. Used for all new quotes.",
  },
};

/**
 * `format_key` written on new pricing snapshots and used for the
 * generic sales pricing form. Legacy `red_iron` snapshots keep their
 * stored key for backward-compatible PDF + edit flows.
 */
export function resolveOpportunityFormatKey(_productType: string): OpportunityFormatKey {
  return "generic";
}

/**
 * `format_key` used when loading manager proposal-format presets
 * (calc lines, deposit rules, proposal text). Matches the Manager
 * → Proposal formats editor: `red_iron` product type rows live under
 * `format_key = red_iron`; every other product type uses `generic`.
 */
export function resolveManagerPresetFormatKey(productType: string): OpportunityFormatKey {
  return productType === "red_iron" ? "red_iron" : "generic";
}
